import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import useToast from '../hooks/useToast';
import ToastContainer from '../components/ToastContainer';

const AdministrativePage = () => {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [requests, setRequests] = useState([]);
  const [, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const { toasts, addToast, removeToast } = useToast();

  // Veri çekme fonksiyonları
  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/administrative/requests');
      if (response.success) {
        setRequests(response.requests);
      }
    } catch (error) {
      console.error('Talep verileri yüklenirken hata:', error);
      addToast('Talep verileri yüklenirken bir hata oluştu', 'error', 5000);
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const categories = [
    { id: 'stationery', name: 'Kırtasiye', icon: 'bx-pencil' },
    { id: 'phone', name: 'Telefon', icon: 'bx-phone' },
    { id: 'computer', name: 'Bilgisayar Aksesuarları', icon: 'bx-laptop' },
    { id: 'other', name: 'Diğer', icon: 'bx-package' }
  ];

  const stationeryItems = [
    'Kalem (Mavi, Siyah, Kırmızı)',
    'Defter (A4, A5)',
    'Dosya',
    'Zımba',
    'Zımba Teli',
    'Post-it',
    'Kağıt (A4)',
    'Fotokopi Kağıdı'
  ];

  const phoneItems = [
    'iPhone',
    'Samsung Galaxy',
    'Telefon Kılıfı',
    'Şarj Aleti',
    'Kulaklık',
    'Powerbank'
  ];

  const computerItems = [
    'Klavye',
    'Mouse',
    'Monitör',
    'USB Kablo',
    'HDMI Kablo',
    'Laptop Çantası',
    'Webcam',
    'Hoparlör'
  ];

  const [formData, setFormData] = useState({
    item: '',
    quantity: 1,
    priority: 'normal',
    description: '',
    department: '',
    requestedBy: '',
    requestDate: new Date().toISOString().split('T')[0]
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      const requestData = {
        ...formData,
        category: selectedCategory,
        status: 'pending'
      };
      
      const response = await api.post('/api/administrative/requests', requestData);
      
      if (response.success) {
        addToast('Talep başarıyla oluşturuldu!', 'success', 5000);
        // Form sıfırlama
        setFormData({
          item: '',
          quantity: 1,
          priority: 'normal',
          description: '',
          department: '',
          requestedBy: '',
          requestDate: new Date().toISOString().split('T')[0]
        });
        setSelectedCategory('');
        // Verileri yenile
        fetchRequests();
      }
    } catch (error) {
      console.error('Talep oluşturulurken hata:', error);
      addToast('Talep oluşturulurken bir hata oluştu', 'error', 5000);
    } finally {
      setSubmitting(false);
    }
  };

  const getItemsByCategory = () => {
    switch (selectedCategory) {
      case 'stationery':
        return stationeryItems;
      case 'phone':
        return phoneItems;
      case 'computer':
        return computerItems;
      default:
        return [];
    }
  };

  return (
    <div className="modern-hr-page">
      <div className="container-fluid">
        <div className="modern-hr-header">
          <div className="modern-hr-header-content">
            <div className="modern-hr-header-text">
              <h1>İdari İşler</h1>
              <p>Kırtasiye, telefon, bilgisayar aksesuarları ve diğer ihtiyaçlarınız için talep oluşturun</p>
            </div>
            <div className="modern-hr-header-actions">
              <div className="modern-hr-stats">
                <div className="modern-hr-stat-item">
                  <i className="bx bx-file-plus"></i>
                  <span>{requests.length} Talep</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="modern-hr-widget">
          <div className="modern-hr-widget-header">
            <div className="modern-hr-widget-title">
              <i className="bx bx-file-plus"></i>
              <h4>Talep Formu</h4>
            </div>
            <div className="modern-hr-widget-actions">
              <span className="modern-hr-count">
                Yeni Talep
              </span>
            </div>
          </div>

          <div className="modern-hr-form">
            <form onSubmit={handleSubmit}>
                {/* Kategori Seçimi */}
                <div className="modern-hr-section">
                  <div className="modern-hr-section-title">
                    <i className="bx bx-category"></i>
                    Talep Kategorisi
                  </div>
                  <div className="row">
                    {categories.map((category) => (
                      <div key={category.id} className="col-md-3 col-sm-6 mb-3">
                        <div 
                          className={`modern-category-card ${selectedCategory === category.id ? 'selected' : ''}`}
                          onClick={() => setSelectedCategory(category.id)}
                        >
                          <i className={`bx ${category.icon}`}></i>
                          <h6>{category.name}</h6>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Ürün Seçimi */}
                {selectedCategory && (
                  <div className="row mb-4">
                    <div className="col-12">
                      <label className="form-label">Ürün Seçimi</label>
                      <select 
                        className="form-select"
                        value={formData.item}
                        onChange={(e) => setFormData({...formData, item: e.target.value})}
                        required
                      >
                        <option value="">Ürün seçin</option>
                        {getItemsByCategory().map((item, index) => (
                          <option key={index} value={item}>{item}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Miktar ve Öncelik */}
                <div className="row">
                  <div className="col-md-6">
                    <div className="modern-hr-form-group">
                      <label className="modern-hr-form-label">Miktar</label>
                      <input 
                        type="number" 
                        className="modern-hr-form-input"
                        min="1"
                        value={formData.quantity}
                        onChange={(e) => setFormData({...formData, quantity: parseInt(e.target.value)})}
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="modern-hr-form-group">
                      <label className="modern-hr-form-label">Öncelik</label>
                      <select 
                        className="modern-hr-form-select"
                        value={formData.priority}
                        onChange={(e) => setFormData({...formData, priority: e.target.value})}
                      >
                        <option value="low">Düşük</option>
                        <option value="normal">Normal</option>
                        <option value="high">Yüksek</option>
                        <option value="urgent">Acil</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Açıklama */}
                <div className="modern-hr-form-group">
                  <label className="modern-hr-form-label">Açıklama</label>
                  <textarea 
                    className="modern-hr-form-textarea"
                    rows="4"
                    placeholder="Talep detaylarını açıklayın..."
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                  />
                </div>

                {/* Departman ve Talep Eden */}
                <div className="row">
                  <div className="col-md-6">
                    <div className="modern-hr-form-group">
                      <label className="modern-hr-form-label">Departman</label>
                      <select 
                        className="modern-hr-form-select"
                        value={formData.department}
                        onChange={(e) => setFormData({...formData, department: e.target.value})}
                        required
                      >
                        <option value="">Departman seçin</option>
                        <option value="hr">İnsan Kaynakları</option>
                        <option value="it">Bilgi İşlem</option>
                        <option value="finance">Mali İşler</option>
                        <option value="procurement">Satın Alma</option>
                        <option value="operations">Operasyon</option>
                        <option value="other">Diğer</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="modern-hr-form-group">
                      <label className="modern-hr-form-label">Talep Eden</label>
                      <input 
                        type="text" 
                        className="modern-hr-form-input"
                        placeholder="Ad Soyad"
                        value={formData.requestedBy}
                        onChange={(e) => setFormData({...formData, requestedBy: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Talep Tarihi */}
                <div className="modern-hr-form-group">
                  <label className="modern-hr-form-label">Talep Tarihi</label>
                  <input 
                    type="date" 
                    className="modern-hr-form-input"
                    value={formData.requestDate}
                    onChange={(e) => setFormData({...formData, requestDate: e.target.value})}
                    required
                  />
                </div>

                {/* Gönder Butonu */}
                <div className="modern-hr-form-actions">
                  <button 
                    type="submit" 
                    className="modern-hr-btn modern-hr-btn-primary"
                    disabled={submitting}
                  >
                    <i className="bx bx-send"></i>
                    {submitting ? 'Gönderiliyor...' : 'Talep Oluştur'}
                  </button>
                </div>
              </form>
            </div>
          </div>

        {/* Yan Panel - Talep Durumu */}
        <div className="col-lg-4 col-md-12">
          <div className="box">
            <div className="box-header">
              <h4 className="mb-0">Talep Durumu</h4>
            </div>
            <div className="box-body">
              <div className="request-status">
                <div className="status-item">
                  <div className="status-icon bg-primary">
                    <i className="bx bx-edit"></i>
                  </div>
                  <div className="status-content">
                    <h6>Talep Oluşturuldu</h6>
                    <small className="text-muted">Talep sisteme kaydedildi</small>
                  </div>
                </div>
                
                <div className="status-item">
                  <div className="status-icon bg-warning">
                    <i className="bx bx-time"></i>
                  </div>
                  <div className="status-content">
                    <h6>İnceleme Aşaması</h6>
                    <small className="text-muted">Departman tarafından inceleniyor</small>
                  </div>
                </div>
                
                <div className="status-item">
                  <div className="status-icon bg-info">
                    <i className="bx bx-check"></i>
                  </div>
                  <div className="status-content">
                    <h6>Onaylandı</h6>
                    <small className="text-muted">Talep onaylandı</small>
                  </div>
                </div>
                
                <div className="status-item">
                  <div className="status-icon bg-success">
                    <i className="bx bx-package"></i>
                  </div>
                  <div className="status-content">
                    <h6>Teslim Edildi</h6>
                    <small className="text-muted">Ürün teslim edildi</small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Hızlı İstatistikler */}
          <div className="box mt-4">
            <div className="box-header">
              <h4 className="mb-0">Bu Ay</h4>
            </div>
            <div className="box-body">
              <div className="stats-grid">
                <div className="stat-item">
                  <h5 className="text-primary">12</h5>
                  <small>Toplam Talep</small>
                </div>
                <div className="stat-item">
                  <h5 className="text-success">8</h5>
                  <small>Tamamlanan</small>
                </div>
                <div className="stat-item">
                  <h5 className="text-warning">3</h5>
                  <small>Beklemede</small>
                </div>
                <div className="stat-item">
                  <h5 className="text-danger">1</h5>
                  <small>Reddedilen</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default AdministrativePage;
